import React, { useState, useRef,useEffect, Dispatch,SetStateAction,useCallback  } from 'react';
import {RocketIcon , Clock, AlertCircle, CheckCircle, Loader,X, Globe, MoreVertical, Plus, CirclePlus, Check, ChevronUp, ChevronDown,ChevronRight, Trash2, Loader2, CheckCircle2,Bot ,Award, DiscIcon,CircleDot,Info, Minus, RefreshCw     } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faCheck ,faTimes, faGlobe,faTrash,faCirclePlus,faEllipsisV,faRobot } from '@fortawesome/free-solid-svg-icons'; 

import {  WebsiteData,ExcludedItems, LinkType } from "../../types/CourseData";

const WebsiteTreeView: React.FC<{
  link: LinkType;
  level: number;
  isLast: boolean;
  baseurl: string;
  parentExpanded?: boolean;
  checkedMap: Record<string, boolean>;
  setCheckedMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  disabledMap: Record<string, boolean>;
  setDisabledMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  globalExpanded?: boolean;
  onToggleExpanded?: (linkPage: string, expanded: boolean) => void;
}> = ({ 
  link, 
  level, 
  isLast, 
  baseurl, 
  parentExpanded, 
  checkedMap, 
  setCheckedMap, 
  disabledMap, 
  setDisabledMap,
  globalExpanded,
  onToggleExpanded
}) => {
  const [expanded, setExpanded] = useState(parentExpanded || false);
  const [allExpanded, setAllExpanded] = useState(false);
  const hasChildren = Array.isArray(link?.links) && link.links.length > 0;
  const currentBaseUrl = baseurl;

  const isChecked = checkedMap[link.page] ?? true;

  useEffect(() => {
    if (parentExpanded) {
      setExpanded(true);
    }
  }, [parentExpanded]);

  // Handle global expand/collapse for level 1+ items
  useEffect(() => {
    if (level >= 1 && globalExpanded !== undefined) {
      setExpanded(globalExpanded);
    }
  }, [globalExpanded, level]);

  useEffect(() => {
    // Never disable the main parent (level 0)
    if (level === 0) {
      const updatedDisabledMap = { ...disabledMap };
      updatedDisabledMap[link.page] = false;
      setDisabledMap(updatedDisabledMap);
      return;
    }

    // If parent is unchecked, ensure children are disabled
    if (hasChildren && checkedMap[link.page] === false) {
      const updatedDisabledMap = { ...disabledMap };

      const disableChildren = (links: LinkType[]) => {
        for (const child of links) {
          updatedDisabledMap[child.page] = true;
          if (child.links?.length) {
            disableChildren(child.links);
          }
        }
      };

      if (link.links) {
          disableChildren(link.links);
      }
      setDisabledMap(updatedDisabledMap);
    }
    // If parent is checked, enable children (remove disabled state)
    else if (hasChildren && checkedMap[link.page] === true) {
      const updatedDisabledMap = { ...disabledMap };

      const enableChildren = (links: LinkType[]) => {
        for (const child of links) {
          updatedDisabledMap[child.page] = false;
          if (child.links?.length) {
            enableChildren(child.links);
          }
        }
      };

      if (link.links) {
          enableChildren(link.links);
      }
      setDisabledMap(updatedDisabledMap);
    }
  }, [checkedMap, link, hasChildren, level]);

  const updateChildren = (
    links: LinkType[],
    checked: boolean,
    map: Record<string, boolean>,
    disabled: Record<string, boolean>
  ) => {
    for (const child of links) {
      map[child.page] = checked;
      disabled[child.page] = !checked; // disable if unchecked, enable if checked
      if (child.links?.length) {
        updateChildren(child.links, checked, map, disabled);
      }
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Never allow main parent (level 0) to be disabled
    if (disabledMap[link.page] && level !== 0) return;
    
    const newChecked = e.target.checked;
    const updatedCheckedMap = { ...checkedMap, [link.page]: newChecked };
    const updatedDisabledMap = { ...disabledMap };

    // Always ensure main parent is enabled
    if (level === 0) {
      updatedDisabledMap[link.page] = false;
    }

    if (hasChildren && link.links) {
      updateChildren(link.links, newChecked, updatedCheckedMap, updatedDisabledMap);
    }

    setCheckedMap(updatedCheckedMap);
    setDisabledMap(updatedDisabledMap);
  };

  const handleLocalExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggleExpanded?.(link.page, newExpanded);
  };

  const getDisplayText = () => {
    if (level === 0) return link.page;
    try {
      const urlObj = new URL(link.page);
      const base = urlObj.origin; // e.g., https://www.test.com

      if (urlObj.href.includes(base)) {
        const path = urlObj.href.replace(base, ""); // e.g., /home/page1
        return `...${path}`;
      } else {
        return link.page;
      }
    } catch {
      return link.page;
    }
  };

  return (
    <li style={{ marginLeft: `${level * 20}px`, marginTop: "8px" }} className="pt-3">
      <div
        onClick={() => hasChildren && handleLocalExpand()}
        style={{
          cursor: hasChildren ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Individual expand/collapse arrow */}
        <span style={{ marginRight: "4px" }}>
          {hasChildren ? (expanded ? "▼" : "▶") : "🔗"}
        </span>

        <label className="flex items-center cursor-pointer pl-5">
            <input
              type="checkbox"
              checked={isChecked}
              disabled={level === 0 ? false : disabledMap[link.page]}
              onChange={handleCheckboxChange}
              className="hidden"
            />

            <div
              className={`mr-2 h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center ${
                String(isChecked) === 'true' ? "bg-black" : "bg-white"
              } ${(level === 0 ? false : disabledMap[link.page]) ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {String(isChecked) === 'true' && (
                <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />
              )}
            </div>
          </label>
        <a
          href={link.page}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontWeight: hasChildren ? "bold" : "normal",
            textDecoration: "none",
            color: "inherit",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {getDisplayText()}
        </a>
      </div>

      {expanded && hasChildren && (
        <ul style={{ marginLeft: "1rem" }}>
          {link.links?.map((child:any, idx:any) => (
            <WebsiteTreeView
              key={idx}
              link={child}
              level={level + 1}
              isLast={idx === (link.links?.length ?? 0) - 1}
              baseurl={baseurl}
              parentExpanded={expanded}
              checkedMap={checkedMap}
              setCheckedMap={setCheckedMap}
              disabledMap={disabledMap}
              setDisabledMap={setDisabledMap}
              globalExpanded={globalExpanded}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default WebsiteTreeView;